<script lang="ts">
import { defineComponent, h, withDirectives, vShow, computed, shallowRef, nextTick, unref } from 'vue';

import { classNames, convertToUnit } from '@components/utils';

import { CdsTransition, useGroupItem, useLazy, useRender, useSsrBoot } from '../../composable';

import { CdsSlideViewGroupSymbol, useInjectSlideView } from './context';
import { makeSlideViewItemProps } from './props';

export const componentName = 'CdsSlideViewItem';
export default defineComponent({
  name: componentName,
  props: {
    ...makeSlideViewItemProps()
  },
  emits: {
    'group:selected': (value: { value: boolean }) => true
  },
  setup(props, { slots, expose }) {
    const slideView = useInjectSlideView();
    const groupItem = useGroupItem(props, CdsSlideViewGroupSymbol);
    const { isBooted } = useSsrBoot();

    if (!slideView || !groupItem) {
      throw new Error('CdsSlideViewItem must be used inside a CdsSlideView');
    }

    const isTransitioning = shallowRef(false);
    const hasTransition = computed(
      () =>
        unref(isBooted) &&
        (unref(slideView.isReversed) ? props.reverseTransition !== false : props.transition !== false)
    );

    function onAfterTransition() {
      if (!unref(isTransitioning) || !slideView) return;

      isTransitioning.value = false;
      if (slideView.transitionCount.value > 0) {
        slideView.transitionCount.value -= 1;

        if (slideView.transitionCount.value === 0) {
          slideView.transitionHeight.value = undefined;
        }
      }
    }

    function onBeforeTransition() {
      if (unref(isTransitioning) || !slideView) return;

      isTransitioning.value = true;
      if (slideView.transitionCount.value === 0) {
        slideView.transitionHeight.value = convertToUnit(unref(slideView.rootRef)?.clientHeight);
      }

      slideView.transitionCount.value += 1;
    }

    function onTransitionCancelled() {
      onAfterTransition();
    }

    function onEnterTransition(el: Element) {
      if (!unref(isTransitioning)) return;

      nextTick(() => {
        if (!unref(hasTransition) || !unref(isTransitioning) || !slideView) return;

        slideView.transitionHeight.value = convertToUnit(el.clientHeight);
      });
    }

    const transition = computed(() => {
      const name = unref(slideView.isReversed) ? props.reverseTransition : props.transition;

      return !unref(hasTransition)
        ? false
        : {
            name: typeof name !== 'string' ? unref(slideView.transition) : name,
            onBeforeEnter: onBeforeTransition,
            onAfterEnter: onAfterTransition,
            onEnterCancelled: onTransitionCancelled,
            onBeforeLeave: onBeforeTransition,
            onAfterLeave: onAfterTransition,
            onLeaveCancelled: onTransitionCancelled,
            onEnter: onEnterTransition
          };
    });

    const { hasContent } = useLazy(props, groupItem.isSelected);

    useRender(() =>
      h(
        CdsTransition,
        {
          transition: unref(transition),
          disabled: !unref(isBooted)
        },
        {
          default: () =>
            withDirectives(
              h(
                'div',
                {
                  class: classNames('cds-slide-view-item', unref(groupItem.selectedClass))
                },
                unref(hasContent) && slots.default?.()
              ),
              [[vShow, unref(groupItem.isSelected)]]
            )
        }
      )
    );

    expose({
      groupItem
    });

    return {};
  }
});
</script>
